import { useEffect, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import { Formik, Form } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import { updateSubscription } from '../../reducers/subscription';
import { completeStep } from '../../reducers/steps';
import { updateCities } from '../../reducers/cities';
import api from '../../services/api';
import { formatCellphone } from '../../utils/formatters';
import { validateZipcode, validateCellphone } from '../../utils/validators';
import { loadShipping } from '../../services/shipping';
import { trackPageView, trackGaEvent } from '../../services/tracking';
import { updateReducerAndForm } from '../../services/subscription';
import { sendError } from '../../services/errorTracking';

import { Row, Col } from '../grid';
import { Button } from '../button';
import { Input } from '../input';
import { FormActions } from '../formActions';
import { PageHeader } from '../pageHeader';
import ShippingSummary from './ShippingSummary';

import Summary from '../summary';

const Delivery = ({ history }) => {
  const formikRef = useRef();
  const dispatch = useDispatch();

  const subscription = useSelector((state) => state.subscription);
  const states = useSelector((state) => state.states);
  const cities = useSelector((state) => state.cities);

  const initialValues = { ...subscription };

  const handleZipcodeBlur = (e) => {
    updateReducerAndForm({
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      stateId: '',
      state: '',
      cityId: '',
      city: '',
    }, formikRef.current.setFieldValue);

    if(validateZipcode(e.target.value)){
      calculateShipping();
      loadAddress();
    }
  }

  const handleStateChange = (e) => {
    if(e.target.value){
      let state = states.find((state) => {
        return parseInt(state.id) === parseInt(e.target.value);
      });

      updateReducerAndForm({
        stateId: state.id,
        state: state.abbreviation,
      }, formikRef.current.setFieldValue);

      loadCities(state.id);
    }
    else {
      updateReducerAndForm({
        stateId: '',
        state: '',
        cityId: '',
        city: ''
      }, formikRef.current.setFieldValue);
    }
  }

  const handleCityChange = (e) => {
    if(e.target.value){
      let city = cities.find((city) => {
        return parseInt(city.id) === parseInt(e.target.value);
      });

      updateReducerAndForm({
        cityId: city.id,
        city: city.name,
      }, formikRef.current.setFieldValue);
    }
    else{
      updateReducerAndForm({
        cityId: '',
        city: '',
      }, formikRef.current.setFieldValue);
    }
  }

  const calculateShipping = () => {
    if(!validateZipcode(formikRef.current.values.zipcode)) return;

    loadShipping(formikRef.current.values.zipcode, formikRef.current.values.quantity, (response) => {
      let data = response.data;

      if(data && data.id){
        updateReducerAndForm({
          shippingPrice: data.discounted_price,
          deliveryTime: data.delivery_time,
          itemsToFreeShipping: data.items_to_free_shipping,
        }, formikRef.current.setFieldValue);
      }
    });
  }

  const loadCities = (stateId) => {
    if(!stateId) return;

    if(stateId !== formikRef.current.values.stateId){
      updateReducerAndForm({
        cityId: '',
        city: '',
      }, formikRef.current.setFieldValue);
    }

    api.get('/subscriptions/cities', {
      params: {
        state_id: stateId
      }
    }).then((response) => {
      dispatch(updateCities(response.data.cities));
    }).catch((e) => {
      sendError('ERROR: /subscriptions/cities', { message: e.message });
    });
  }

  const loadAddress = () => {
    if(!validateZipcode(formikRef.current.values.zipcode)) return;

    api.get('/subscriptions/find-address', {
      params: {
        zipcode: formikRef.current.values.zipcode
      }
    }).then((response) => {
      const address = response.data.address;

      if(address && formikRef.current){
        updateReducerAndForm({
          street: address.address,
          neighborhood: address.neighborhood,
          stateId: address.state_id,
          state: address.state,
          cityId: address.city_id,
          city: address.city,
        }, formikRef.current.setFieldValue);

        loadCities(address.state_id);
      }
    }).catch((e) => {
      sendError('ERROR: /subscriptions/find-address', { message: e.message });
    });
  }

  const validate = (values) => {
    let errors = {};

    if(!values.zipcode){
      errors.zipcode = 'Digite o seu CEP';
    }
    else if(!validateZipcode(values.zipcode)){
      errors.zipcode = 'Digite um CEP válido';
    }
    if(!values.street){
      errors.street = 'Digite o seu endereço';
    }
    if(!values.number){
      errors.number = 'Digite o número da sua residência';
    }
    if(!values.neighborhood){
      errors.neighborhood = 'Digite o seu bairro';
    }
    if(!values.stateId){
      errors.stateId = 'Escolha o seu estado';
    }
    if(!values.cityId){
      errors.cityId = 'Escolha a sua cidade';
    }
    if(!values.cellphone){
      errors.cellphone = 'Digite o seu telefone';
    }
    else if(!validateCellphone(values.cellphone)){
      errors.cellphone = 'Digite um telefone válido';
    }

    return errors;
  }

  const handleSubmit = (values) => {
    dispatch(updateSubscription(values));
    dispatch(completeStep(5));
    history.push('/pagamento');

    trackGaEvent('add_shipping_info');
  }

  useEffect(() => {
    calculateShipping();
    loadAddress();

    trackPageView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validate={validate}
      >
        {({ values }) => (
          <Form>
            <PageHeader>
              <small>Endereço de entrega</small>
              <h1>Para onde devemos enviar?</h1>
              <p>Basta nos informar seu endereço que enviaremos com muito cuidado.</p>
            </PageHeader>

            <Row>
              <Col>
                <Input
                  name="zipcode"
                  format="#####-###"
                  label="CEP"
                  onBlur={handleZipcodeBlur}
                />
              </Col>
              <Col>
                <ShippingSummary
                  shippingPrice={values.shippingPrice}
                  deliveryTime={values.deliveryTime}
                />
              </Col>
            </Row>

            <Input
              name="street"
              label="Endereço"
            />

            <Row>
              <Col>
                <Input
                  name="number"
                  label="Número"
                />
              </Col>
              <Col>
                <Input
                  name="complement"
                  label="Complemento"
                />
              </Col>
            </Row>

            <Input
              name="neighborhood"
              label="Bairro"
            />

            <Row>
              <Col>
                <Input
                  name="stateId"
                  as="select"
                  label="Estado"
                  options={states}
                  onChange={handleStateChange}
                />
              </Col>
              <Col>
                <Input
                  name="cityId"
                  as="select"
                  label="Cidade"
                  options={cities}
                  onChange={handleCityChange}
                />
              </Col>
            </Row>

            <Input
              name="cellphone"
              format={formatCellphone}
              label="Telefone"
            />

            <Summary />

            <FormActions backUrl="/plano-gateway" backDescription="Sua caixinha">
              <Button
                type="submit"
                icon="angle-right"
                align="right"
                description="Pagamento"
              >
                Continuar
              </Button>
            </FormActions>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default withRouter(Delivery);
